import type { JSX, ReactNode, ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';
import type { BoxProps } from '../Box';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import type { Spacings } from '../../css/designTokens/spacing';
import type { BorderRadius } from '../../css/designTokens/borderRadius';
import { Box } from '../Box';
import { getColorScheme } from '../../private/helpers/getColorScheme';

type StyledCardProps = BoxProps & {
  $backgroundColor: string;
  $borderColor: string;
  $borderRadius: BorderRadius;
  $colorScheme: ColorSchemeTypes;
  $height: string | undefined;
  $paddingSize: string;
  $width: string | undefined;
};

const StyledCard = styled(Box)<StyledCardProps>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  box-sizing: border-box;
  padding: ${({ $paddingSize }) => `var(--wui-${$paddingSize})`};
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  outline: 2px solid ${({ $borderColor }) => $borderColor};
  outline-offset: -2px;
  border-radius: ${({ $borderRadius }) => `var(--wui-${$borderRadius})`};
  display: flex;
  height: ${({ $height }) => $height};
  width: ${({ $width }) => $width};
`;

const prominenceMap = {
  primary: {
    backgroundColor: 'var(--wui-color-bg-surface)',
    borderColor: 'var(--wui-color-border)',
  },
  secondary: {
    backgroundColor: 'var(--wui-color-bg-surface-secondary)',
    borderColor: 'var(--wui-color-border-secondary)',
  },
  tertiary: {
    backgroundColor: 'var(--wui-color-bg-surface-tertiary)',
    borderColor: 'var(--wui-color-border-secondary)',
  },
};

export type CardProps = BoxProps &
  ComponentPropsWithoutRef<'div'> & {
    /**
     * Content to be rendered inside the Card component
     */
    children: ReactNode;
    /**
     * Specifies the background and border color of the card.
     */
    prominence?: 'primary' | 'secondary' | 'tertiary';
    /**
     * Determines the color scheme for the card and its children.
     */
    colorScheme?: ColorSchemeTypes;
    /**
     * Specifies the internal padding of the card. Uses the design system's spacing scale.
     */
    paddingSize?: Spacings;
    /**
     * When true, adds a border around the card based on its colorScheme and prominence.
     */
    border?: boolean;

    /**
     * Border radius to apply to the Card
     */
    borderRadius?: BorderRadius;
    /**
     * Specifies the gaps (gutters) between rows and columns
     */
    gap?: BoxProps['gap'];
    /**
     * Specifies the height of the card.
     */
    height?: string;
    /**
     * Specifies the width of the card.
     */
    width?: string;
    /**
     * @ignore
     */
    alignContent?: BoxProps['alignContent'];
    /**
     * @ignore
     */
    alignItems?: BoxProps['alignItems'];
    /**
     * @ignore
     */
    alignSelf?: BoxProps['alignSelf'];
    /**
     * @ignore
     */
    basis?: BoxProps['basis'];
    /**
     * @ignore
     */
    direction?: BoxProps['direction'];
    /**
     * @ignore
     */
    fill?: BoxProps['fill'];
    /**
     * @ignore
     */
    fillViewport?: BoxProps['fillViewport'];
    /**
     * @ignore
     */
    flexMode?: BoxProps['flexMode'];
    /**
     * @ignore
     */
    grow?: BoxProps['grow'];
    /**
     * @ignore
     */
    inline?: BoxProps['inline'];
    /**
     * @ignore
     */
    justifyContent?: BoxProps['justifyContent'];
    /**
     * @ignore
     */
    order?: BoxProps['order'];
    /**
     * @ignore
     */
    shrink?: BoxProps['shrink'];
    /**
     * @ignore
     */
    wrapItems?: BoxProps['wrapItems'];
  };
/**
 * A flexible container component that groups related content with consistent spacing and styling.
 * Cards can be used to visually distinguish content sections. Extends the [Box component.](https://wistia.github.io/vhs/storybook-ui/?path=/docs/components-box--docs)
 */
export const Card = ({
  children,
  border = false,
  borderRadius = 'border-radius-04',
  colorScheme = 'inherit',
  direction = 'column',
  gap = 'space-03',
  height,
  paddingSize = 'space-04',
  prominence = 'secondary',
  width,
  ...props
}: CardProps): JSX.Element => (
  <StyledCard
    $backgroundColor={prominenceMap[prominence].backgroundColor}
    $borderColor={border ? prominenceMap[prominence].borderColor : 'transparent'}
    $borderRadius={borderRadius}
    $colorScheme={colorScheme}
    $height={height}
    $paddingSize={paddingSize}
    $width={width}
    direction={direction}
    gap={gap}
    {...props}
  >
    {children}
  </StyledCard>
);

Card.displayName = 'Card';
